/**
 * MobileParticipantLayout — master wrapper for mobile participant view.
 * 4-zone layout: header + KPI + scrollable content + bottom nav.
 * Renders tab content based on active mobile tab.
 * Full-screen takeover for Navigator voice conversations.
 */

import { useState, useCallback, useEffect } from 'react'
import { MobileHeader } from './MobileHeader'
import { MobileBottomNav, type MobileTab } from './MobileBottomNav'
import { MobileKPIBar } from './MobileKPIBar'
import { NavigatorVoiceConversation } from '@/components/navigator/NavigatorVoiceConversation'
import { NavigatorTextConversation } from '@/components/navigator/NavigatorTextConversation'
import { supabase } from '@/lib/supabase'

interface SimState {
  current_phase: string
  current_round: number
  status: string
}

interface RoleData {
  id: string
  character_name: string
  title: string
  country_code: string
  positions: string[]
  position_type: string
  status: string
  status_detail?: { cause?: string }
}

interface CountryData {
  sim_name: string
  color_ui: string
  stability: number
  treasury: number
  gdp: number
  nuclear_level: number
  ai_level: number
}

interface Props {
  simId: string
  simState: SimState | null
  remaining: number | null
  myRole: RoleData | null
  myCountry: CountryData | null
  profile: { display_name?: string } | null
  color: string
  round: number
  hasRole: boolean
  parentNotifications: unknown[]
  artefacts: { is_read: boolean }[]
  broadcast: string | null
  setBroadcast: (b: string | null) => void
  // KPI data
  kpiData: {
    stability: number; deltaStability: number | null
    treasury: number; deltaTreasury: number | null
    gdpRank: number; deltaGdpRank: number | null
    milRank: number; deltaMilRank: number | null
    nukeRank: number | null; deltaNukeRank: number | null; nukeLevel: number
    aiRank: number | null; deltaAiRank: number | null; aiLevel: number
    wars: string[]
  } | null
  // Desktop tab content renderer — reuses existing tab components
  renderTabContent: (tab: string) => React.ReactNode
  // Called when user taps a bottom nav tab — parent can clear active action
  onMobileTabChange?: () => void
  // Override tab on fresh role assignment or no-role state
  initialTab?: MobileTab
}

export function MobileParticipantLayout({
  simId, simState, remaining, myRole, myCountry, profile, color, round, hasRole,
  parentNotifications, artefacts, broadcast, setBroadcast, kpiData, renderTabContent, onMobileTabChange, initialTab,
}: Props) {
  const [activeTab, setActiveTab] = useState<MobileTab>(() => {
    // Priority: 1) explicit initialTab (fresh assignment, no-role) 2) sessionStorage 3) default
    if (initialTab) { sessionStorage.setItem('mobileTab', initialTab); return initialTab }
    const stored = sessionStorage.getItem('mobileTab') as MobileTab | null
    if (stored && (hasRole || stored === 'world')) return stored
    return hasRole ? 'actions' : 'world'
  })
  // Persist tab selection so it survives landscape rotation (useIsMobile remount)
  const switchTab = (tab: MobileTab) => { setActiveTab(tab); sessionStorage.setItem('mobileTab', tab) }
  const [navSession, setNavSession] = useState<{
    conversation_id: string; voice_agent_id: string; prompt: string; first_message: string; conversation_type: string
  } | null>(null)
  const [navLoading, setNavLoading] = useState(false)
  const [navNudge, setNavNudge] = useState(false)
  const [navModeSelector, setNavModeSelector] = useState(false)
  const [navMode, setNavMode] = useState<'voice' | 'text' | null>(null)

  // Navigator nudge check
  useEffect(() => {
    if (!simId || !hasRole) return
    const check = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) return
        const resp = await fetch(`/api/sim/${simId}/navigator/history`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        })
        if (!resp.ok) return
        const json = await resp.json()
        const h = json.data
        const status = simState?.status || ''
        const needsIntro = (status === 'pre_start' || status === 'setup' || status === 'active') && !h.intro_complete
        const needsOutro = status === 'completed' && !h.outro_complete
        setNavNudge(needsIntro || needsOutro)
      } catch { /* */ }
    }
    check()
  }, [simId, simState?.status, hasRole])

  const startNavWithMode = useCallback(async (mode: 'voice' | 'text') => {
    setNavModeSelector(false)
    setNavMode(mode)
    setNavLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session || !myRole) return
      const resp = await fetch(`/api/sim/${simId}/navigator/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ role_id: myRole.id }),
      })
      if (!resp.ok) return
      setNavSession((await resp.json()).data)
    } catch { /* */ }
    finally { setNavLoading(false) }
  }, [simId, myRole])

  // Handle Navigator tab — show mode selector
  const handleTabChange = useCallback(async (tab: MobileTab) => {
    if (tab === 'navigator') {
      if (navLoading || navSession || !myRole) return
      setNavModeSelector(true)
      return
    }
    onMobileTabChange?.()
    switchTab(tab)
  }, [simId, myRole, navLoading, navSession, onMobileTabChange, switchTab])

  const actionsBadge = parentNotifications.length || undefined
  const unreadBadge = artefacts.filter(a => !a.is_read).length || undefined

  // Map mobile tab IDs to desktop tab IDs for content rendering
  const desktopTab = activeTab === 'confidential' ? 'confidential' : activeTab

  return (
    <div className="flex flex-col h-[100dvh] bg-base overflow-x-hidden">
      {/* Header */}
      <MobileHeader
        characterName={myRole?.character_name || ''}
        countryCode={myRole?.country_code || ''}
        color={color}
        round={round}
        phase={simState?.current_phase || 'pre'}
        remaining={remaining}
        status={simState?.status || ''}
        hasRole={hasRole}
        displayName={profile?.display_name}
        onNameTap={() => { onMobileTabChange?.(); switchTab('actions') }}
      />

      {/* Banners */}
      {broadcast && (
        <div className="bg-warning/10 border-b border-warning/30 px-3 py-2 flex items-center gap-2">
          <span className="font-body text-[10px] font-bold text-warning uppercase">Broadcast</span>
          <span className="font-body text-caption text-text-primary flex-1">{broadcast}</span>
          <button onClick={() => setBroadcast(null)} className="text-text-secondary text-body-sm px-1">{'\u2715'}</button>
        </div>
      )}

      {myRole?.status === 'arrested' && (
        <div className="bg-danger/10 border-b border-danger/30 px-3 py-2">
          <p className="font-heading text-body-sm text-danger font-semibold">You Are Under Arrest</p>
          <p className="font-body text-caption text-text-secondary">You cannot take any actions while detained.</p>
        </div>
      )}

      {myRole?.status === 'killed' && (
        <div className="bg-danger/10 border-b border-danger/30 px-3 py-2">
          <p className="font-heading text-body-sm text-danger font-semibold">
            {myRole.status_detail?.cause === 'health' ? 'Your Leader Has Passed Away' : 'Your Character Has Been Eliminated'}
          </p>
        </div>
      )}

      {simState?.status === 'paused' && (
        <div className="bg-warning/10 border-b border-warning/30 px-3 py-1.5 text-center">
          <span className="font-body text-caption font-bold text-warning uppercase tracking-wider">Simulation Paused</span>
        </div>
      )}

      {/* KPI Bar */}
      {hasRole && <MobileKPIBar data={kpiData} />}

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-3 py-3" style={{ WebkitOverflowScrolling: 'touch' as never }}>
        {!hasRole ? (
          <div>
            <div className="bg-action/10 border border-action/20 rounded-lg p-4 mb-3 text-center">
              <p className="font-heading text-body-sm text-action font-semibold">Awaiting Role Assignment</p>
              <p className="font-body text-caption text-text-secondary mt-1">The moderator will assign your role shortly. Explore the world below.</p>
            </div>
            {renderTabContent('world')}
          </div>
        ) : (
          renderTabContent(desktopTab)
        )}
      </main>

      {/* Navigator Mode Selector */}
      {navModeSelector && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">🧭</span>
              <h3 className="font-heading text-h3 text-text-primary">Navigator</h3>
            </div>
            <p className="font-body text-body-sm text-text-secondary mb-4">Choose how to communicate:</p>
            <div className="flex gap-3">
              <button onClick={() => startNavWithMode('voice')}
                className="flex-1 bg-action/10 text-action font-body text-body-sm font-medium py-3 rounded-lg hover:bg-action/20 min-h-[48px]">
                🎙️ Voice
              </button>
              <button onClick={() => startNavWithMode('text')}
                className="flex-1 bg-accent/10 text-accent font-body text-body-sm font-medium py-3 rounded-lg hover:bg-accent/20 min-h-[48px]">
                💬 Text
              </button>
            </div>
            <button onClick={() => setNavModeSelector(false)}
              className="w-full mt-3 font-body text-caption text-text-secondary hover:text-text-primary py-2">Cancel</button>
          </div>
        </div>
      )}

      {/* Navigator Voice */}
      {navSession && navMode === 'voice' && (
        <NavigatorVoiceConversation
          voiceAgentId={navSession.voice_agent_id}
          prompt={navSession.prompt}
          firstMessage={navSession.first_message}
          conversationType={navSession.conversation_type}
          onEnd={async (transcript, transcriptJson, durationSeconds) => {
            try {
              const { data: { session } } = await supabase.auth.getSession()
              if (session) {
                await fetch(`/api/sim/${simId}/navigator/end`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                  body: JSON.stringify({ conversation_id: navSession.conversation_id, transcript, transcript_json: transcriptJson, duration_seconds: durationSeconds }),
                })
              }
            } catch { /* */ }
            setNavSession(null); setNavMode(null); setNavNudge(false)
          }}
          onClose={() => { setNavSession(null); setNavMode(null) }}
        />
      )}

      {/* Navigator Text */}
      {navSession && navMode === 'text' && (
        <NavigatorTextConversation
          simId={simId}
          conversationId={navSession.conversation_id}
          systemPrompt={navSession.prompt}
          firstMessage={navSession.first_message}
          conversationType={navSession.conversation_type}
          onEnd={async (transcript, duration) => {
            try {
              const { data: { session } } = await supabase.auth.getSession()
              if (session) {
                await fetch(`/api/sim/${simId}/navigator/end`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                  body: JSON.stringify({ conversation_id: navSession.conversation_id, transcript, transcript_json: [], duration_seconds: duration }),
                })
              }
            } catch { /* */ }
            setNavSession(null); setNavMode(null); setNavNudge(false)
          }}
        />
      )}

      {/* Bottom Nav */}
      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        hasRole={hasRole}
        actionsBadge={actionsBadge}
        unreadBadge={unreadBadge}
        navNudge={navNudge}
      />
    </div>
  )
}
